import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { AUTH_ERROR_MESSAGES, ROLE_LABELS, isAuthErrorCode, isRoleCode } from '@thrivehrm/shared';
import { CalendarDays, ReceiptText, UserRound } from 'lucide-react';
import { z } from 'zod';
import { Logo } from '@/components/shell/logo';
import { PersonAvatar } from '@/components/ui/avatar';
import { Banner } from '@/components/ui/banner';
import { Button } from '@/components/ui/button';
import { ApiError } from '@/lib/api';
import { devLogin, loadDevAccounts, safeNext } from '@/lib/session';

const searchSchema = z.object({
  /** Denial code from the Google callback (FR-AUTH-3/4). */
  error: z.string().optional().catch(undefined),
  /** Why the user is here: the session ended, or they just signed out. */
  reason: z.enum(['expired', 'signed_out']).optional().catch(undefined),
  /** Same-origin path to open after sign-in. */
  next: z.string().optional().catch(undefined),
});

export const Route = createFileRoute('/login')({
  validateSearch: searchSchema,
  beforeLoad: ({ context, search }) => {
    // Already signed in: go where they were heading.
    if (context.auth.user) throw redirect({ href: safeNext(search.next) });
  },
  component: LoginPage,
});

/**
 * Sign-in page (DESIGN_SYSTEM §6 Login, US-01, FR-AUTH-1..8; layout T-101): brand panel beside
 * the sign-in column (stacked on phones) with "Continue with Google", the reason a sign-in was
 * refused, session-expired / signed-out notices, and — only where the API enables it — the
 * "Dev login as…" picker.
 */
function LoginPage() {
  const { error, reason, next } = Route.useSearch();
  const target = safeNext(next);
  const googleHref = `/api/v1/auth/google${target === '/' ? '' : `?next=${encodeURIComponent(target)}`}`;
  const message = isAuthErrorCode(error)
    ? AUTH_ERROR_MESSAGES[error]
    : error
      ? AUTH_ERROR_MESSAGES.sign_in_failed
      : null;

  return (
    <div className="flex min-h-dvh flex-col bg-background md:flex-row">
      <BrandPanel />
      <main className="flex flex-1 items-center justify-center px-4 py-10 sm:px-6 md:flex-[1_1_32.5rem] md:py-12">
        <section aria-labelledby="login-title" className="flex w-full max-w-105 flex-col gap-6">
          <div className="flex flex-col gap-1.5">
            <h1 id="login-title" className="text-h1">
              Sign in to ThriveHRM
            </h1>
            <p className="text-body text-muted-foreground">
              One click with your Google account — no password to remember.
            </p>
          </div>
          {message ? (
            <Banner tone="danger" title="Couldn't sign you in" live>
              {message}
            </Banner>
          ) : null}
          {!message && reason === 'expired' ? (
            <Banner tone="info" title="Your session ended" live>
              Your session expired. Sign in again to continue.
            </Banner>
          ) : null}
          {!message && reason === 'signed_out' ? (
            <Banner tone="success" live>
              You have signed out.
            </Banner>
          ) : null}
          <Button asChild size="lg" className="w-full">
            <a href={googleHref}>
              {/* Google's mark stays on white (brand guidelines); --ink-on-brand is white in both themes. */}
              <span className="flex size-7 items-center justify-center rounded-sm bg-on-brand">
                <GoogleMark />
              </span>
              Continue with Google
            </a>
          </Button>
          <p className="text-small text-muted-foreground">
            Use your V2 email. No V2 email? Use the alternate email HR registered for you.
          </p>
          <DevLogin next={target} />
        </section>
      </main>
    </div>
  );
}

const BENEFITS = [
  { icon: UserRound, text: 'Keep your profile, contact and bank details up to date' },
  { icon: CalendarDays, text: 'Apply for leave or work from home and follow each approval' },
  { icon: ReceiptText, text: 'See your attendance and your payslips' },
] as const;

/**
 * Brand panel (design E1-web-login): ThriveHRM's own name and a factual summary of what the app
 * does. On phones only the logo and headline show, so the sign-in button stays near the top.
 */
function BrandPanel() {
  return (
    <aside
      aria-label="About ThriveHRM"
      className="flex flex-col justify-between gap-6 bg-brand px-4 py-6 text-on-brand sm:px-6 md:flex-[1_1_28rem] md:gap-10 md:px-14 md:py-12"
    >
      <div className="flex flex-col gap-1">
        <Logo tone="on-brand" />
        <span className="text-small">V2 Technologies HR</span>
      </div>
      <div className="flex max-w-120 flex-col gap-7">
        <p className="text-h2 md:text-display">Your profile, leave and pay in one place.</p>
        <ul className="hidden flex-col gap-4 text-item font-normal md:flex">
          {BENEFITS.map(({ icon: Icon, text }) => (
            <li key={text} className="flex items-center gap-3">
              <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-on-brand/14">
                <Icon aria-hidden="true" className="size-5" />
              </span>
              {text}
            </li>
          ))}
        </ul>
      </div>
      <p className="hidden text-small md:block">Need help signing in? Contact HR.</p>
    </aside>
  );
}

/** FR-AUTH-8: shown only when the API answers `/auth/dev/accounts` (dev, flag on). */
function DevLogin({ next }: { next: string }) {
  const accounts = useQuery({
    queryKey: ['auth', 'dev-accounts'],
    queryFn: loadDevAccounts,
    retry: false,
  });
  const login = useMutation({ mutationFn: (userId: string) => devLogin(userId, next) });
  if (!accounts.data) return null;
  return (
    <div className="flex flex-col gap-3 border-t pt-5">
      <div>
        <h2 className="text-section text-foreground">Dev login as…</h2>
        <p className="text-small text-muted-foreground">Development only — no Google needed.</p>
      </div>
      {login.error ? (
        <Banner tone="danger" live>
          {login.error instanceof ApiError ? login.error.message : 'Sign-in failed.'}
        </Banner>
      ) : null}
      <ul
        className="flex max-h-80 flex-col divide-y overflow-y-auto rounded-lg border bg-surface"
        aria-label="Dev accounts"
      >
        {accounts.data.map((a) => (
          <li key={a.userId}>
            {/* min-h, not h-auto: tailwind-merge does not know `h-control-*`, so an h-auto
                override would lose to the Button's fixed 40 px height. */}
            <Button
              variant="ghost"
              className="min-h-16 w-full justify-start gap-3 rounded-none px-4 py-2.5 text-left"
              loading={login.isPending && login.variables === a.userId}
              disabled={login.isPending}
              onClick={() => {
                login.mutate(a.userId);
              }}
            >
              <PersonAvatar name={a.name} size="sm" />
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="truncate text-body-strong text-foreground">
                  {a.name}{' '}
                  <span className="text-small font-normal text-muted-foreground">#{a.empCode}</span>
                </span>
                <span className="truncate text-small font-normal text-muted-foreground">
                  {a.email} ·{' '}
                  {a.roles
                    .filter(isRoleCode)
                    .map((r) => ROLE_LABELS[r])
                    .join(', ')}
                </span>
              </span>
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Google "G" mark (brand guidelines: original colours, next to the label). */
function GoogleMark() {
  return (
    <svg aria-hidden="true" viewBox="0 0 48 48" className="size-5">
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.5-4.5 2.4-7.2 2.4-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"
      />
    </svg>
  );
}
